<script setup lang="ts">
/**
 * A responsive grid of `NxTile`s (and an `NxAddTile` at its end): as many
 * columns as fit, each at least `size` wide, all tiles of a row equally tall.
 * On a phone it is one column.
 *
 * The grid is a list and its tiles are the list items, so screen readers
 * announce „Liste, 3 Einträge“; `label` names the list.
 */
import { provide } from 'vue';
import { tileGridKey } from './context';

export type NxTileGridSize = 'xs' | 'sm' | 'md' | 'lg';

const props = withDefaults(
  defineProps<{
    /** Minimum tile width. Defaults to `md`. */
    size?: NxTileGridSize;
    /** Accessible name of the list. */
    label?: string;
  }>(),
  { size: 'md', label: undefined }
);

provide(tileGridKey, true);
</script>

<template>
  <div
    class="nx-tile-grid"
    :class="`tile-${props.size}`"
    role="list"
    :aria-label="props.label"
    data-testid="nx-tile-grid"
  >
    <slot />
  </div>
</template>

<style scoped>
.nx-tile-grid {
  --tile-min: var(--nxt-panel-width-md);

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, var(--tile-min)), 1fr));
  gap: var(--nxt-space-sm);
}
.nx-tile-grid.tile-sm {
  --tile-min: var(--nxt-panel-width-sm);
}
.nx-tile-grid.tile-xs {
  --tile-min: var(--nxt-area-size-md);
}
.nx-tile-grid.tile-lg {
  --tile-min: calc(var(--nxt-panel-width-md) + var(--nxt-panel-width-xs) / 2);
}
</style>
